import {
  DynamicSupplyHardware,
  SimpleClusterForm
} from "@/types";
import {
  SAVED_DEPLOYMENT_CONFIG_SCHEMA_VERSION,
  SavedDeploymentConfig,
  SavedDeploymentConfigKind,
  SavedDeploymentConfigPayload,
  SavedDeploymentConfigStorage
} from "@/types/savedDeploymentConfig";
import { v4 as uuidv4 } from "uuid";

export const SAVED_DEPLOYMENT_CONFIGS_STORAGE_KEY = "SAVED_DEPLOYMENT_CONFIGS";
export const SAVED_DEPLOYMENT_CONFIG_ANONYMOUS_USER = "anonymous";
export const SAVED_DEPLOYMENT_CONFIG_MAX_PER_KIND = 25;

const ENV_FIELD_PREFIXES = [
  "env_normal_key_",
  "env_normal_value_",
  "env_private_key_",
  "env_private_value_"
];

const SHARED_PAYLOAD_KEYS = [
  "name",
  "hardware",
  "hardwareQuantity",
  "locations",
  "supplier",
  "durationType",
  "durationQuantity",
  "hardwareId",
  "hardwareName",
  "location",
  "numCards"
] as const;

const CONTAINER_PAYLOAD_KEYS = [
  "containerImage",
  "containerRecipe",
  "isPrivate",
  "username",
  "secret",
  "command",
  "trafficPort",
  "healthCheckPath",
  "healthCheckIntervalSeconds",
  "healthCheckTimeoutSeconds",
  "healthCheckGracePeriodSeconds",
  "gpusPerContainer",
  "gpuUnavailabilityPolicy",
  "replicaCount"
] as const;

const VM_PAYLOAD_KEYS = [
  "vmImageType",
  "isConfidentialCompute",
  "sshType",
  "selectedSshKeyIds",
  "githubIds",
  "networkServices"
] as const;

export const isEnvFieldKey = (key: string) =>
  ENV_FIELD_PREFIXES.some((prefix) => key.startsWith(prefix));

export const clearEnvVariableFields = (values: Record<string, unknown>) => {
  Object.keys(values).forEach((key) => {
    if (isEnvFieldKey(key)) {
      delete values[key];
    }
  });
};

const pickDefined = <T extends Record<string, unknown>>(
  source: Record<string, unknown>,
  keys: readonly string[]
): Partial<T> => {
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as Partial<T>;
};

export const toSavedPayload = (
  values: SimpleClusterForm,
  kind: SavedDeploymentConfigKind,
  hardware?: DynamicSupplyHardware | null
): SavedDeploymentConfigPayload => {
  const source = values as unknown as Record<string, unknown>;
  const keys =
    kind === "container"
      ? [...SHARED_PAYLOAD_KEYS, ...CONTAINER_PAYLOAD_KEYS]
      : [...SHARED_PAYLOAD_KEYS, ...VM_PAYLOAD_KEYS];

  const payload: SavedDeploymentConfigPayload = {
    ...pickDefined(source, keys)
  };

  // Dynamic env vars (container only)
  if (kind === "container") {
    Object.keys(source).forEach((key) => {
      if (isEnvFieldKey(key) && source[key] !== undefined) {
        payload[key] = source[key];
      }
    });
  }

  // Hardware match hints for rematch when ids rotate
  if (hardware) {
    payload.hardwareId = hardware.hardwareId;
    payload.hardwareName = hardware.hardwareName;
    payload.location = hardware.location;
    payload.supplier = hardware.supplier;
    payload.numCards = hardware.numCards;
  } else if (values.hardware) {
    payload.hardwareId = values.hardware;
  }

  // Canonical form field
  if (payload.hardwareId && !payload.hardware) {
    payload.hardware = payload.hardwareId;
  }

  return payload;
};

const payloadKeysForKind = (kind: SavedDeploymentConfigKind): readonly string[] =>
  kind === "container"
    ? [...SHARED_PAYLOAD_KEYS, ...CONTAINER_PAYLOAD_KEYS]
    : [...SHARED_PAYLOAD_KEYS, ...VM_PAYLOAD_KEYS];

const isEmptyComparable = (value: unknown): boolean =>
  value === undefined || value === null || value === "";

const normalizeComparableScalar = (value: unknown): unknown => {
  if (isEmptyComparable(value)) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
  }
  if (typeof value === "number" && Number.isNaN(value)) return undefined;
  return value;
};

const normalizeSshKeys = (value: unknown): unknown[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const normalized = value
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const key = normalizeComparableScalar((entry as { key?: unknown }).key);
      const sshValue = normalizeComparableScalar((entry as { value?: unknown }).value);
      if (key === undefined && sshValue === undefined) return null;
      return { key, value: sshValue };
    })
    .filter(Boolean);
  return normalized.length > 0 ? normalized : undefined;
};

const normalizeNetworkServices = (value: unknown): unknown[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const normalized = value
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const service = entry as {
        name?: unknown;
        port?: unknown;
        protocol?: unknown;
        whitelistIps?: unknown;
      };
      const name = normalizeComparableScalar(service.name);
      const port = normalizeComparableScalar(service.port);
      const protocol = normalizeComparableScalar(service.protocol);
      const whitelistIps = Array.isArray(service.whitelistIps)
        ? service.whitelistIps
            .map((ip) => normalizeComparableScalar(ip))
            .filter((ip) => ip !== undefined)
            .sort()
        : undefined;
      if (
        name === undefined &&
        port === undefined &&
        protocol === undefined &&
        whitelistIps === undefined
      ) {
        return null;
      }
      return { name, port, protocol, whitelistIps };
    })
    .filter(Boolean);
  return normalized.length > 0 ? normalized : undefined;
};

const normalizePayloadField = (key: string, value: unknown): unknown => {
  if (key === "sshKeys") return normalizeSshKeys(value);
  if (key === "selectedSshKeyIds" && Array.isArray(value)) {
    const ids = value
      .map((item) => normalizeComparableScalar(item))
      .filter((item): item is string => typeof item === "string");
    return ids.length > 0 ? [...ids].sort() : undefined;
  }
  if (key === "networkServices") return normalizeNetworkServices(value);
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => normalizeComparableScalar(item))
      .filter((item) => item !== undefined);
    return normalized.length > 0 ? normalized : undefined;
  }
  return normalizeComparableScalar(value);
};

const HINT_ONLY_PAYLOAD_KEYS = new Set(["hardwareName", "location", "numCards"]);

const normalizeSavedPayload = (
  payload: SavedDeploymentConfigPayload,
  kind: SavedDeploymentConfigKind
): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};

  for (const key of payloadKeysForKind(kind)) {
    if (HINT_ONLY_PAYLOAD_KEYS.has(key)) continue;
    const value = normalizePayloadField(key, payload[key]);
    if (value !== undefined) {
      normalized[key] = value;
    }
  }

  if (kind === "container") {
    Object.keys(payload)
      .sort()
      .forEach((key) => {
        if (!isEnvFieldKey(key)) return;
        const value = normalizePayloadField(key, payload[key]);
        if (value !== undefined) {
          normalized[key] = value;
        }
      });
  }

  const hardwareId = normalizeComparableScalar(payload.hardwareId ?? payload.hardware);
  if (hardwareId !== undefined) {
    normalized.hardwareId = hardwareId;
    normalized.hardware = hardwareId;
  }

  return normalized;
};

const stableStringify = (value: unknown): string => {
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    return `{${entries
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

/** True when two serialized payloads represent the same saved configuration. */
export const savedDeploymentPayloadsEqual = (
  current: SavedDeploymentConfigPayload,
  saved: SavedDeploymentConfigPayload,
  kind: SavedDeploymentConfigKind
): boolean =>
  stableStringify(normalizeSavedPayload(current, kind)) ===
  stableStringify(normalizeSavedPayload(saved, kind));

export const applySavedPayload = (
  currentValues: SimpleClusterForm,
  payload: SavedDeploymentConfigPayload
): SimpleClusterForm => {
  const next = { ...(currentValues as unknown as Record<string, unknown>) };
  clearEnvVariableFields(next);

  Object.entries(payload).forEach(([key, value]) => {
    // Hardware hints are applied separately via matchSavedHardware / applyHardwareToForm
    if (
      key === "hardwareId" ||
      key === "hardwareName" ||
      key === "location" ||
      key === "numCards"
    ) {
      return;
    }
    if (value !== undefined) {
      next[key] = value;
    }
  });

  return next as unknown as SimpleClusterForm;
};

export type MatchSavedHardwareResult =
  | { status: "matched"; hardware: DynamicSupplyHardware }
  | { status: "missing" }
  | { status: "sold_out"; hardware: DynamicSupplyHardware };

export const matchSavedHardware = (
  payload: SavedDeploymentConfigPayload,
  hardwareList: DynamicSupplyHardware[]
): MatchSavedHardwareResult => {
  const byId = payload.hardwareId || payload.hardware;
  if (byId) {
    const match = hardwareList.find((h) => h.hardwareId === byId);
    if (match) {
      return match.isSoldOut
        ? { status: "sold_out", hardware: match }
        : { status: "matched", hardware: match };
    }
  }

  if (payload.hardwareName) {
    const nameLower = payload.hardwareName.toLowerCase();
    const candidates = hardwareList.filter((h) =>
      h.hardwareName.toLowerCase().includes(nameLower)
    );
    const narrowed = candidates.filter((h) => {
      if (payload.location && h.location !== payload.location) return false;
      if (payload.supplier && h.supplier !== payload.supplier) return false;
      return true;
    });
    const match = (narrowed.length > 0 ? narrowed : candidates)[0];
    if (match) {
      return match.isSoldOut
        ? { status: "sold_out", hardware: match }
        : { status: "matched", hardware: match };
    }
  }

  return { status: "missing" };
};

export const emptySavedDeploymentConfigStorage = (): SavedDeploymentConfigStorage => ({
  version: 1,
  byUserId: {}
});

export const parseSavedDeploymentConfigStorage = (
  raw: unknown
): SavedDeploymentConfigStorage => {
  if (!raw || typeof raw !== "object") {
    return emptySavedDeploymentConfigStorage();
  }
  const data = raw as Partial<SavedDeploymentConfigStorage>;
  if (data.version !== 1 || !data.byUserId || typeof data.byUserId !== "object") {
    return emptySavedDeploymentConfigStorage();
  }
  return {
    version: 1,
    byUserId: data.byUserId
  };
};

export const listConfigsForUser = (
  storage: SavedDeploymentConfigStorage,
  userId: string,
  kind?: SavedDeploymentConfigKind
): SavedDeploymentConfig[] => {
  const list = storage.byUserId[userId] ?? [];
  const filtered = kind ? list.filter((c) => c.kind === kind) : list;
  return [...filtered].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
};

export const findConfigByName = (
  storage: SavedDeploymentConfigStorage,
  userId: string,
  kind: SavedDeploymentConfigKind,
  name: string,
  excludeId?: string
): SavedDeploymentConfig | undefined => {
  const normalized = name.trim().toLowerCase();
  return (storage.byUserId[userId] ?? []).find(
    (c) =>
      c.kind === kind &&
      c.name.trim().toLowerCase() === normalized &&
      c.id !== excludeId
  );
};

export type CreateSavedConfigInput = {
  name: string;
  kind: SavedDeploymentConfigKind;
  payload: SavedDeploymentConfigPayload;
  id?: string;
};

export const createSavedConfigRecord = (
  input: CreateSavedConfigInput,
  now = new Date().toISOString()
): SavedDeploymentConfig => ({
  id: input.id ?? uuidv4(),
  name: input.name.trim(),
  kind: input.kind,
  schemaVersion: SAVED_DEPLOYMENT_CONFIG_SCHEMA_VERSION,
  createdAt: now,
  updatedAt: now,
  payload: input.payload
});

export const upsertSavedConfigInStorage = (
  storage: SavedDeploymentConfigStorage,
  userId: string,
  config: SavedDeploymentConfig,
  options?: { overwriteId?: string }
): SavedDeploymentConfigStorage => {
  const existing = storage.byUserId[userId] ?? [];
  let nextList = [...existing];

  if (options?.overwriteId) {
    nextList = nextList.filter((c) => c.id !== options.overwriteId);
  } else {
    // Replace same id if present
    nextList = nextList.filter((c) => c.id !== config.id);
  }

  // Enforce soft cap per kind (keep most recently updated)
  const sameKind = nextList.filter((c) => c.kind === config.kind);
  if (sameKind.length >= SAVED_DEPLOYMENT_CONFIG_MAX_PER_KIND) {
    const sorted = [...sameKind].sort(
      (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
    );
    const toRemove = sorted.slice(
      0,
      sameKind.length - SAVED_DEPLOYMENT_CONFIG_MAX_PER_KIND + 1
    );
    const removeIds = new Set(toRemove.map((c) => c.id));
    nextList = nextList.filter((c) => !removeIds.has(c.id));
  }

  nextList.push(config);

  return {
    ...storage,
    byUserId: {
      ...storage.byUserId,
      [userId]: nextList
    }
  };
};

export const updateSavedConfigInStorage = (
  storage: SavedDeploymentConfigStorage,
  userId: string,
  id: string,
  patch: Partial<Pick<SavedDeploymentConfig, "name" | "payload">>
): SavedDeploymentConfigStorage => {
  const list = storage.byUserId[userId] ?? [];
  const now = new Date().toISOString();
  return {
    ...storage,
    byUserId: {
      ...storage.byUserId,
      [userId]: list.map((c) =>
        c.id === id
          ? {
              ...c,
              ...(patch.name !== undefined ? { name: patch.name.trim() } : {}),
              ...(patch.payload !== undefined ? { payload: patch.payload } : {}),
              updatedAt: now
            }
          : c
      )
    }
  };
};

export const deleteSavedConfigFromStorage = (
  storage: SavedDeploymentConfigStorage,
  userId: string,
  id: string
): SavedDeploymentConfigStorage => {
  const list = storage.byUserId[userId] ?? [];
  return {
    ...storage,
    byUserId: {
      ...storage.byUserId,
      [userId]: list.filter((c) => c.id !== id)
    }
  };
};

export const clearConfigsForUser = (
  storage: SavedDeploymentConfigStorage,
  userId: string
): SavedDeploymentConfigStorage => {
  if (!(userId in storage.byUserId)) {
    return storage;
  }
  const nextByUserId = { ...storage.byUserId };
  delete nextByUserId[userId];
  return {
    ...storage,
    byUserId: nextByUserId
  };
};

export const readSavedDeploymentConfigStorage = (): SavedDeploymentConfigStorage => {
  if (typeof localStorage === "undefined") {
    return emptySavedDeploymentConfigStorage();
  }
  try {
    const raw = localStorage.getItem(SAVED_DEPLOYMENT_CONFIGS_STORAGE_KEY);
    if (!raw) return emptySavedDeploymentConfigStorage();
    return parseSavedDeploymentConfigStorage(JSON.parse(raw));
  } catch {
    return emptySavedDeploymentConfigStorage();
  }
};

export const writeSavedDeploymentConfigStorage = (
  storage: SavedDeploymentConfigStorage
): void => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(SAVED_DEPLOYMENT_CONFIGS_STORAGE_KEY, JSON.stringify(storage));
};
